"use client";

import { UsedProductsList as UsedProductList } from "@/@types";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import NoContent from "../common/NoContent";
import { ROUTES } from "@/config/routes"
import { formatCurrency } from "@/utils/string";

type Props = {
  usedProductsList: UsedProductList;
};

export default function UsedProductsList({ usedProductsList }: Props) {
  const pathName = usePathname();
  const products = usedProductsList?.data?.usedProducts;

  if (!products || products.length === 0) {
    return <NoContent message="No se encontraron productos disponibles." />
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
      {products.map((product, index) => {
        const hasPromotion = product.offer === 1 && !!product.customPrice;
        const regularPrice = formatCurrency(product.price, product.currency);
        const promotionPrice = product.customPrice
          ? formatCurrency(product.customPrice, product.customCurrency || product.currency)
          : null;

        return (
          <Link
            href={ROUTES.usedEquipments.used(product.slug)}
            key={index}
            className="flex flex-col h-[380px] bg-white shadow overflow-hidden hover:shadow-2xl transition relative"
          >
          <div className="h-[220px] w-full flex items-center justify-center bg-white">
            <Image
              width={200}
              height={200}
              src={product.image || "/images/img_no_found.jpg"}
              alt={product.name || "Imagen no disponible"}
              className="w-full h-full object-cover"
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              quality={65}
              loading="eager"
              priority={index < 3}
              unoptimized={false}
            />
          </div>

          <div className="flex-1 flex flex-col justify-start bg-light00 relative py-2 pt-2 pb-0">
            <h2 className="text-xl font-bold mb-2 text-dark02 capitalize px-4">{product.name}</h2>
            <div className="space-y-1">
              <div className="flex justify-between items-end text-gray03 px-4">
                <span className="text-sm">Modelo:</span>
                <span className="text-sm font-semibold">{product.model}</span>
              </div>

              <div className="flex justify-between items-end text-gray03 px-4">
                <span className="text-sm">Año:</span>
                <span className="text-sm font-semibold">{product.year}</span>
              </div>

              <div className="flex justify-between items-end text-gray03 px-4">
                <span className="text-sm">Precio:</span>
                {hasPromotion ? (
                  <span className="text-sm font-semibold line-through decoration-red decoration-2">
                    {regularPrice}
                  </span>
                ) : (
                  <span className="text-sm font-semibold">
                    {regularPrice}
                  </span>
                )}
              </div>
            </div>

            {hasPromotion && promotionPrice && (
              <div className="mt-2 bg-red text-white py-2 px-4 flex flex-row items-center justify-between">
                <span className="text-sm">Promoción</span>
                <span className="text-sm font-semibold">
                  {promotionPrice}
                </span>
              </div>
            )}
          </div>
          </Link>
        );
      })}
    </div>
  );
}
